import clsx from 'clsx';
import { Globe, ImageIcon, Mail, Monitor, Palette, Phone, Shield, UploadCloud, User } from 'lucide-react';
import React, { useEffect,useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateFirmMutation } from '@/services/api/firmsService';
import { Firm } from '@/services/types/firms-types';

import { useUpdateClientPortalLogo } from './hooks';

interface FirmClientPortalSettingsProps {
  firm: Firm;
}

interface LogoUploaderProps {
  logoUrl: string | null;
  onUpload: (file: File | null) => void;
  onClear: () => void;
  isLoading?: boolean;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  logoUrl,
  onUpload,
  onClear,
  isLoading = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isLoading) return;
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        onUpload(file);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (isLoading) return;
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  const onButtonClick = () => {
    if (isLoading) return;
    inputRef.current?.click();
  };

  return (
    <div
      className="w-full"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
        className="hidden"
        onChange={handleChange}
        disabled={isLoading}
      />

      {logoUrl ? (
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-4">
            <div className="h-16 w-32 flex items-center justify-center bg-gray-50 rounded border overflow-hidden">
              <img
                src={logoUrl}
                alt="Upload Page Logo"
                className="max-h-full max-w-full object-contain"
              />
            </div>
            <span className="font-medium text-sm text-gray-700">
              Current logo
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onButtonClick}
              disabled={isLoading}
            >
              {isLoading ? 'Replacing...' : 'Replace'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={onClear}
              disabled={isLoading}
            >
              {isLoading ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </div>
      ) : (
        <div
          role="presentation"
          onClick={onButtonClick}
          className={clsx(
            'relative flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors',
            {
              'border-primary bg-primary/5': dragActive && !isLoading,
              'border-border bg-background hover:bg-muted/50':
                !dragActive && !isLoading,
              'cursor-not-allowed bg-muted/50': isLoading,
            },
          )}
        >
          <div className="flex flex-col items-center justify-center pb-6 pt-5">
            <UploadCloud
              className={clsx('mb-4 h-8 w-8', {
                'text-muted-foreground': !dragActive,
                'text-primary': dragActive,
              })}
            />
            <p className="mb-2 text-sm text-muted-foreground">
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </p>
            <p className="text-xs text-muted-foreground">
              PNG, JPG, WebP, or SVG (recommended: 200x50px)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const getPortalDomain = (subdomain: string | null) => {
  const isLocal = import.meta.env.VITE_APP_ENVIRONMENT === 'local';
  if (isLocal) {
    return 'localhost:3001';
  }
  return subdomain ? `${subdomain}.casequill.com` : 'yourfirm.casequill.com';
};

const getPortalUrl = (subdomain: string | null) => {
  const isLocal = import.meta.env.VITE_APP_ENVIRONMENT === 'local';
  if (isLocal) {
    return 'http://localhost:3001';
  }
  return `https://${subdomain || 'yourfirm'}.casequill.com`;
};

export const FirmClientPortalSettings: React.FC<
  FirmClientPortalSettingsProps
> = ({ firm }) => {
  const { handleUpdate: handleLogoUpdate, isLoading: isLogoLoading } =
    useUpdateClientPortalLogo({
      firmId: firm.id,
    });

  const [updateFirm, { isLoading: isUpdating }] = useUpdateFirmMutation();

  // Form state for branding settings
  const [subdomain, setSubdomain] = useState(
    firm.client_portal_subdomain ?? '',
  );
  const [primaryColor, setPrimaryColor] = useState(
    firm.client_portal_primary_color ?? '#2563eb',
  );
  const [secondaryColor, setSecondaryColor] = useState(
    firm.client_portal_secondary_color ?? '#10b981',
  );

  // Track if form has changes
  const hasChanges =
    subdomain !== (firm.client_portal_subdomain ?? '') ||
    primaryColor !== (firm.client_portal_primary_color ?? '#2563eb') ||
    secondaryColor !== (firm.client_portal_secondary_color ?? '#10b981');

  // Reset form when firm data changes
  useEffect(() => {
    setSubdomain(firm.client_portal_subdomain ?? '');
    setPrimaryColor(firm.client_portal_primary_color ?? '#2563eb');
    setSecondaryColor(firm.client_portal_secondary_color ?? '#10b981');
  }, [firm]);

  const handleSaveBranding = async () => {
    try {
      await updateFirm({
        id: firm.id,
        client_portal_subdomain: subdomain || null,
        client_portal_primary_color: primaryColor || null,
        client_portal_secondary_color: secondaryColor || null,
      }).unwrap();
      toast.success('Upload page settings saved');
    } catch {
      toast.error('Failed to save secure upload page settings');
    }
  };

  // Get the logo URL from firm data
  const logoUrl = firm.client_portal_logo_url ?? null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold mb-1">Secure Upload Link Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure the white-label branding for your client document upload
          portal.
        </p>
      </div>

      {/* Subdomain Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-gray-500" />
          <div>
            <Label className="text-base font-medium">Upload Page Subdomain</Label>
            <p className="text-sm text-muted-foreground">
              The unique subdomain for your firm&apos;s secure upload page.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={subdomain}
            onChange={(e) =>
              setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
            }
            placeholder="yourfirm"
            className="max-w-xs font-mono"
          />
          <span className="text-sm text-muted-foreground">
            {import.meta.env.VITE_APP_ENVIRONMENT === 'local' ? '(localhost:3001)' : '.casequill.com'}
          </span>
        </div>
        {subdomain && (
          <p className="text-xs text-muted-foreground">
            Portal URL: {getPortalUrl(subdomain)}/
          </p>
        )}
      </div>

      {/* Logo Upload Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-gray-500" />
          <div>
            <Label className="text-base font-medium">Upload Page Logo</Label>
            <p className="text-sm text-muted-foreground">
              This logo appears in the header of your secure upload page.
            </p>
          </div>
        </div>
        <LogoUploader
          logoUrl={logoUrl}
          onUpload={handleLogoUpdate}
          onClear={() => handleLogoUpdate(null)}
          isLoading={isLogoLoading}
        />
      </div>

      {/* Brand Colors Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-gray-500" />
          <div>
            <Label className="text-base font-medium">Brand Colors</Label>
            <p className="text-sm text-muted-foreground">
              Customize the color theme of your secure upload page.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-8">
          <div className="space-y-2">
            <Label className="text-sm">Primary Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-10 h-10 rounded border cursor-pointer"
              />
              <Input
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#2563eb"
                className="w-28 font-mono text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for buttons and accents
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Secondary Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                className="w-10 h-10 rounded border cursor-pointer"
              />
              <Input
                value={secondaryColor}
                onChange={(e) => setSecondaryColor(e.target.value)}
                placeholder="#10b981"
                className="w-28 font-mono text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Used for highlights and badges
            </p>
          </div>
        </div>
      </div>

      {/* Portal Header Preview */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Monitor className="h-5 w-5 text-gray-500" />
          <div>
            <Label className="text-base font-medium">Upload Page Preview</Label>
            <p className="text-sm text-muted-foreground">
              Live preview of how your secure upload page header will appear.
            </p>
          </div>
        </div>

        {/* Mock Portal Frame */}
        <div className="border rounded-xl overflow-hidden shadow-lg bg-white">
          {/* Browser Chrome */}
          <div className="bg-gray-100 border-b px-3 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 mx-2">
              <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono truncate">
                {getPortalUrl(subdomain || null)}/abc123...
              </div>
            </div>
          </div>

          {/* Portal Content */}
          <div className="bg-gradient-to-b from-gray-50 to-gray-100/50">
            {/* Portal Header - matches actual PortalPage design */}
            <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
              <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
                {/* Firm branding */}
                <div className="flex items-center gap-2.5">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={firm.name}
                      className="h-9 w-auto max-w-[120px] object-contain"
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold text-sm shadow-sm"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                      }}
                    >
                      {firm.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900 leading-tight truncate max-w-[140px]">
                      {firm.full_name || firm.name}
                    </p>
                    <p className="text-xs text-gray-500">Secure Document Portal</p>
                  </div>
                </div>

                {/* Language toggle mock */}
                <div className="flex items-center gap-1 text-xs bg-gray-100 rounded-full px-2 py-1">
                  <span className="font-medium text-gray-700">EN</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">ES</span>
                </div>
              </div>
            </header>

            {/* Portal Body Preview */}
            <div className="max-w-md mx-auto px-4 py-6 space-y-4">
              {/* Welcome badge */}
              <div
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${secondaryColor}20`, color: secondaryColor }}
              >
                <Shield className="w-3 h-3" />
                <span>SECURE DOCUMENT PORTAL</span>
              </div>

              {/* Title */}
              <h1 className="text-xl font-bold text-gray-900">
                Welcome to your secure portal
              </h1>
              <p className="text-sm text-gray-600">
                Please upload the following documents to help us process your case.
              </p>

              {/* Document card mock */}
              <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Photo ID</p>
                      <p className="text-xs text-gray-500">Driver&apos;s license or passport</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    style={{ backgroundColor: primaryColor }}
                    className="text-white text-xs hover:opacity-90"
                  >
                    Upload
                  </Button>
                </div>
              </div>

              {/* Another card mock - completed state */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${secondaryColor}15` }}
                    >
                      <svg
                        className="w-5 h-5"
                        style={{ color: secondaryColor }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Auto Insurance</p>
                      <p className="text-xs" style={{ color: secondaryColor }}>
                        Uploaded
                      </p>
                    </div>
                  </div>
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${secondaryColor}15`, color: secondaryColor }}
                  >
                    Complete
                  </span>
                </div>
              </div>

              {/* Paralegal Contact Card */}
              {firm.managing_paralegal && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                    Questions? Contact your paralegal
                  </p>
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {firm.managing_paralegal.first_name?.charAt(0) ||
                       firm.managing_paralegal.username?.charAt(0) ||
                       <User className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {firm.managing_paralegal.first_name && firm.managing_paralegal.last_name
                          ? `${firm.managing_paralegal.first_name} ${firm.managing_paralegal.last_name}`
                          : firm.managing_paralegal.username || 'Paralegal'}
                      </p>
                      {firm.managing_paralegal.email && (
                        <a
                          href={`mailto:${firm.managing_paralegal.email}`}
                          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mt-1"
                        >
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{firm.managing_paralegal.email}</span>
                        </a>
                      )}
                      {firm.managing_paralegal.phone_numbers?.[0]?.phone_number && (
                        <a
                          href={`tel:${firm.managing_paralegal.phone_numbers[0].phone_number}`}
                          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 mt-1"
                        >
                          <Phone className="w-3 h-3" />
                          <span>{firm.managing_paralegal.phone_numbers[0].phone_number}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Show placeholder if no paralegal assigned */}
              {!firm.managing_paralegal && (
                <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-4 text-center">
                  <User className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">
                    Assign a managing paralegal to show contact info here
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSaveBranding} disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}
    </div>
  );
};
